import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { MapPin, GraduationCap, Shield, Crown, Heart, Sparkles, X } from 'lucide-react'
import type { MatchResultDTO } from '../types'
import { shortlistApi } from '../api/shortlist'
import toast from 'react-hot-toast'

interface Props {
  match: MatchResultDTO
  /** When provided, shows a "Not interested" button that removes the card from the feed. */
  onDismiss?: (id: string) => void
}

export default function ProfileCard({ match, onDismiss }: Props) {
  const p = match.profile
  const initial = p.fullName?.charAt(0)?.toUpperCase() ?? '?'
  const qc = useQueryClient()
  const [slLoading, setSlLoading] = useState(false)
  // A photo URL can go stale (member replaced/removed it) — fall back to the initial, not a broken-image glyph.
  const [photoBroken, setPhotoBroken] = useState(false)

  // Shared shortlist query so hearts reflect server state (cached across all cards)
  const { data: shortlist } = useQuery({
    queryKey: ['shortlist'],
    queryFn: () => shortlistApi.getAll().then((r) => r.data.data),
    staleTime: 60_000,
  })
  const shortlisted = shortlist?.some((s) => s.id === p.id) ?? false

  const handleShortlist = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (slLoading) return
    setSlLoading(true)
    try {
      if (shortlisted) {
        await shortlistApi.remove(p.id)
      } else {
        await shortlistApi.add(p.id)
      }
      await qc.invalidateQueries({ queryKey: ['shortlist'] })
    } catch {
      toast.error('Could not update shortlist')
    }
    setSlLoading(false)
  }

  return (
    <Link
      to={`/profile/${p.id}`}
      className="block card-hover overflow-hidden"
    >
      {/* Photo — rounded rectangle; blurred backdrop keeps faces uncropped */}
      <div className="relative mx-2.5 mt-2.5 rounded-2xl overflow-hidden aspect-[3/3.4] bg-ivory-100">
        {p.avatarUrl && !photoBroken ? (
          <>
            {/* Blurred backdrop fills the arch; foreground contains the full photo so faces are never cropped */}
            <img src={p.avatarUrl} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover blur-xl scale-110 opacity-70 saturate-[0.9]" />
            <img src={p.avatarUrl} alt={p.fullName ?? ''} onError={() => setPhotoBroken(true)} className="relative w-full h-full object-contain saturate-[0.95]" />
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2">
            <span className="font-display text-4xl text-maroon-700/40">{initial}</span>
          </div>
        )}

        {/* Badges top-left — wrap and stay clear of the heart button */}
        <div className="absolute top-2.5 left-2.5 flex flex-wrap gap-1 max-w-[calc(100%-3.75rem)]">
          {match.reason === 'NEW_MEMBER' && (
            <span className="flex items-center gap-0.5 text-[9px] font-bold tracking-wide bg-maroon-700 text-ivory-25 px-2 py-0.5 rounded-full">
              <Sparkles size={8} /> NEW
            </span>
          )}
          {p.isPremium && (
            <span className="flex items-center gap-0.5 text-[9px] font-bold tracking-wide bg-ivory-25/90 text-gold-600 border border-gold-500/60 px-2 py-0.5 rounded-full">
              <Crown size={8} /> PREMIUM
            </span>
          )}
          {p.isVerified && (
            <span className="flex items-center gap-0.5 text-[9px] font-bold tracking-wide bg-ivory-25/90 text-success border border-success/40 px-2 py-0.5 rounded-full">
              <Shield size={8} /> VERIFIED
            </span>
          )}
        </div>

        {/* Shortlist heart top-right */}
        <button
          onClick={handleShortlist}
          disabled={slLoading}
          className="absolute top-2.5 right-2.5 w-11 h-11 rounded-full bg-ivory-25/90 backdrop-blur-sm flex items-center justify-center shadow-sm hover:bg-ivory-25 transition-colors disabled:opacity-60"
        >
          <Heart
            size={15}
            strokeWidth={1.75}
            className={shortlisted ? 'text-maroon-700 fill-maroon-700' : 'text-ink-400'}
          />
        </button>

        {/* Not-interested — bottom-left (only on the curated feed) */}
        {onDismiss && (
          <button
            aria-label="Not interested"
            title="Not interested"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDismiss(p.id) }}
            className="absolute bottom-2 left-2.5 w-11 h-11 rounded-full bg-ivory-25/90 backdrop-blur-sm flex items-center justify-center shadow-sm hover:bg-ivory-25 transition-colors"
          >
            <X size={15} strokeWidth={1.75} className="text-ink-400" />
          </button>
        )}
      </div>

      {/* Info */}
      <div className="px-3 pt-2.5 pb-3.5 text-center">
        {/* Match medallion — moved below the photo so it never overlaps the X / verified badge */}
        {match.matchScore > 0 && (
          <span className="medallion mb-1.5">
            ✦ {Math.min(100, match.matchScore)}% MATCH
          </span>
        )}
        <p className="font-display font-semibold text-ink-900 text-[17px] leading-tight">
          {p.fullName ?? 'Member'}
          {p.age > 0 && <span className="text-ink-400">, {p.age}</span>}
        </p>
        {(p.nativeDistrict || p.educationLevel) && (
          <p className="text-[11px] text-ink-400 mt-1 truncate flex items-center justify-center gap-1">
            {p.nativeDistrict && (
              <span className="inline-flex items-center gap-1">
                <MapPin size={9} className="flex-shrink-0" />
                {p.nativeDistrict}
              </span>
            )}
            {p.nativeDistrict && p.educationLevel && <span>·</span>}
            {p.educationLevel && (
              <span className="inline-flex items-center gap-1">
                <GraduationCap size={9} className="flex-shrink-0" />
                {p.educationLevel.replace(/_/g, ' ')}
              </span>
            )}
          </p>
        )}
      </div>
    </Link>
  )
}
